/**
 * StartScreen - who is playing, and one tap to begin.
 *
 * The tap matters technically as well as socially: getUserMedia and the
 * AudioContext both need a user gesture, and speech synthesis is blocked
 * before one on most mobile browsers. Everything the game needs is opened on
 * this single press.
 *
 * WHY A CHILD PICKER IS HERE AT ALL
 * Section 9.1: the chart tracks the CHILD, not the game. Every attempt is
 * keyed on child_id, so a game with no child selected has nowhere to write.
 * Rather than refuse to run - which would be the wrong answer for a parent
 * trying it out before creating a profile - the game offers practice mode and
 * says plainly that nothing will be recorded.
 */

import { useEffect, useState } from 'react';
import { childrenApi } from '../../../../utils/api';

export default function StartScreen({ game, onStart, onExit, speechError, synthSupported }) {
  const [children, setChildren] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    childrenApi.list()
      .then((data) => {
        if (cancelled) return;
        const list = data.children || [];
        setChildren(list);
        if (list.length === 1) setSelected(list[0]);
      })
      .catch((err) => { if (!cancelled) setLoadError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const begin = async () => {
    if (starting) return;
    setStarting(true);
    await onStart(selected);
  };

  return (
    <div className="bp-start">
      <div className="bp-start__sky" aria-hidden="true">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <span key={i} className={`bp-start__drift bp-start__drift--${i}`} />
        ))}
      </div>

      <div className="bp-start__panel">
        <h1 className="bp-start__title">Bubble Pop</h1>
        <p className="bp-start__tagline">Say what you see. The bubble pops.</p>

        {loading && <p className="bp-start__note">Loading…</p>}

        {!loading && children.length > 0 && (
          <div className="bp-start__children">
            <span className="bp-start__label">Who is playing?</span>
            <div className="bp-start__child-list">
              {children.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`bp-start__child${selected && selected.id === c.id ? ' is-selected' : ''}`}
                  onClick={() => setSelected(c)}
                >
                  <span className="bp-start__child-name">{c.name}</span>
                  <span className="bp-start__child-where">
                    Phase {c.current_phase || 1} · Level {c.current_level || 1}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {!loading && children.length === 0 && !loadError && (
          <p className="bp-start__note">
            No child profiles yet. You can play now — nothing will be recorded —
            or add a child from the dashboard first so their progress is kept.
          </p>
        )}

        {loadError && (
          <p className="bp-start__note bp-start__note--warn">
            Could not load profiles ({loadError}). You can still play; nothing
            will be recorded.
          </p>
        )}

        <button
          type="button"
          className="bp__btn bp__btn--primary bp-start__go"
          onClick={begin}
          disabled={starting}
        >
          {starting ? 'Getting ready…' : selected ? `Play with ${selected.name}` : 'Play'}
        </button>

        {!selected && !loading && (
          <p className="bp-start__practice">Practice mode — no progress is recorded.</p>
        )}

        <ul className="bp-start__facts">
          <li>Covers phases {game.phasesAvailable.join(', ')} of the ten.</li>
          <li>Played entirely with your voice. There is nothing to press.</li>
          <li>The microphone stays on for the whole session.</li>
        </ul>

        {speechError && <p className="bp-start__note bp-start__note--warn">{speechError}</p>}
        {!synthSupported && (
          <p className="bp-start__note bp-start__note--warn">
            This browser has no speech voice, so Pip cannot say the words aloud.
            Phase 1 needs that; the prompts will be shown as text instead.
          </p>
        )}

        <button type="button" className="bp__btn bp__btn--quiet" onClick={onExit}>
          Back to games
        </button>
      </div>
    </div>
  );
}
